/**
 * Accessibility Utilities
 * @module utils/starnexus/accessibility
 *
 */

// ============================================================================
// ARIA Label Helpers
// ============================================================================

/**
 * Generate descriptive ARIA label for buttons with icons
 */
export const getButtonAriaLabel = (action: string, target?: string, context?: string): string => {
  const parts = [action];
  if (target) parts.push(target);
  if (context) parts.push(`in ${context}`);
  return parts.join(' ');
};

/**
 * Generate ARIA label for status indicators
 */
export const getStatusAriaLabel = (status: string, itemName?: string): string => {
  if (itemName) {
    return `${itemName}: ${status}`;
  }
  return `Status: ${status}`;
};

/**
 * Generate ARIA label for progress indicators
 */
export const getProgressAriaLabel = (percentage: number, itemName?: string, action?: string): string => {
  const actionText = action || 'Progress';
  if (itemName) {
    return `${itemName}: ${actionText} ${percentage}% complete`;
  }
  return `${actionText}: ${percentage}% complete`;
};

/**
 * Generate ARIA label for metric cards
 */
export const getMetricAriaLabel = (title: string, value: string | number, trend?: { direction: 'up' | 'down' | 'neutral'; percentage: number }): string => {
  let label = `${title}: ${value}`;
  if (trend) {
    const trendText = trend.direction === 'up' ? `increased by ${trend.percentage}%` : trend.direction === 'down' ? `decreased by ${trend.percentage}%` : 'unchanged';
    label += `, ${trendText}`;
  }
  return label;
};

/**
 * Generate ARIA label for table actions
 */
export const getTableActionAriaLabel = (action: string, itemName: string, itemType: string = 'item'): string => {
  return `${action} ${itemType}: ${itemName}`;
};

/**
 * Generate ARIA label for navigation items
 */
export const getNavItemAriaLabel = (text: string, isActive: boolean, hasSubItems?: boolean): string => {
  const parts = [text];
  if (isActive) parts.push('(current page)');
  if (hasSubItems) parts.push('(has submenu)');
  return parts.join(' ');
};

/**
 * Generate ARIA label for file upload areas
 */
export const getFileUploadAriaLabel = (fileCount: number, maxFiles: number, maxSize: string): string => {
  if (fileCount === 0) {
    return `File upload area. Drag and drop files here or click to browse. Maximum ${maxFiles} files, ${maxSize} each.`;
  }
  return `File upload area. ${fileCount} files selected. Maximum ${maxFiles} files, ${maxSize} each.`;
};

/**
 * Generate ARIA label for modal dialogs
 */
export const getModalAriaLabel = (title: string, description?: string): string => {
  if (description) {
    return `${title}. ${description}`;
  }
  return title;
};

// ============================================================================
// Live Region Announcements
// ============================================================================

/**
 * Announce a message to screen readers via aria-live region
 * Creates a temporary element that is announced and then removed
 */
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite'): void => {
  // Find or create the live region container
  let liveRegion = document.getElementById('sr-live-region');

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'sr-live-region';
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(liveRegion);
  }

  // Update priority if needed
  liveRegion.setAttribute('aria-live', priority);

  // Clear and set new message (this triggers the announcement)
  liveRegion.textContent = '';
  // Use setTimeout to ensure the DOM update is processed
  setTimeout(() => {
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }, 100);
};

/**
 * Announce processing status updates
 */
export const announceProcessingUpdate = (itemName: string, status: 'started' | 'progress' | 'completed' | 'failed', progress?: number): void => {
  let message: string;

  switch (status) {
    case 'started':
      message = `Processing started for ${itemName}`;
      break;
    case 'progress':
      message = `${itemName}: ${progress}% complete`;
      break;
    case 'completed':
      message = `${itemName} processing completed successfully`;
      break;
    case 'failed':
      message = `${itemName} processing failed`;
      break;
    default:
      message = `${itemName}: ${status}`;
  }

  announceToScreenReader(message);
};

/**
 * Announce form validation errors
 */
export const announceValidationError = (fieldName: string, errorMessage: string): void => {
  announceToScreenReader(`Error in ${fieldName}: ${errorMessage}`, 'assertive');
};

/**
 * Announce successful actions
 */
export const announceSuccess = (action: string, itemName?: string): void => {
  const message = itemName ? `${action} successful for ${itemName}` : `${action} successful`;
  announceToScreenReader(message);
};

/**
 * Announce navigation changes
 */
export const announceNavigation = (pageName: string): void => {
  announceToScreenReader(`Navigated to ${pageName}`);
};

// ============================================================================
// Keyboard Navigation Helpers
// ============================================================================

/**
 * Check if an element is focusable
 */
export const isFocusable = (element: HTMLElement): boolean => {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ];

  return focusableSelectors.some((selector) => element.matches(selector));
};

/**
 * Get all focusable elements within a container
 */
export const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ].join(', ');

  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors));
};

/**
 * Trap focus within a container (useful for modals)
 */
export const trapFocus = (container: HTMLElement): (() => void) => {
  const focusableElements = getFocusableElements(container);
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return;

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    }
  };

  container.addEventListener('keydown', handleKeyDown);

  // Focus the first element
  firstElement?.focus();

  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
};

// ============================================================================
// Skip Link Helper
// ============================================================================

/**
 * Create a skip link for keyboard navigation
 */
export const createSkipLink = (targetId: string, text: string = 'Skip to main content'): HTMLAnchorElement => {
  const skipLink = document.createElement('a');
  skipLink.href = `#${targetId}`;
  skipLink.textContent = text;
  skipLink.className = 'sr-skip-link';
  skipLink.style.cssText = `
    position: absolute;
    top: -40px;
    left: 0;
    background: #000;
    color: #fff;
    padding: 8px 16px;
    z-index: 10000;
    text-decoration: none;
    transition: top 0.3s;
  `;

  // Show on focus
  skipLink.addEventListener('focus', () => {
    skipLink.style.top = '0';
  });

  skipLink.addEventListener('blur', () => {
    skipLink.style.top = '-40px';
  });

  return skipLink;
};

// ============================================================================
// Form Field Helpers
// ============================================================================

/**
 * Generate unique ID for form field associations
 */
export const generateFieldId = (prefix: string): string => {
  return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Get ARIA describedby value for form fields with multiple descriptions
 */
export const getAriaDescribedBy = (...ids: (string | undefined | null)[]): string | undefined => {
  const validIds = ids.filter(Boolean);
  return validIds.length > 0 ? validIds.join(' ') : undefined;
};
